import "./OrderSummary.scss";

import { Header, Title, Text, Subheader } from "../Typography";
import { useContext } from "react";
import { Button } from "../Button";
import useResponsive from "../../hooks/useResponsive";
import {
  FileUploadContext,
  FileUploadContextProps,
} from "../../Contexts/FileUploadContext";
import { OrderContext, OrderContextProps } from "../../Contexts/OrderContext";
import {
  AddressContext,
  AddressContextProps,
} from "../../Contexts/AddressContext";
import AddressTypeEnum from "../../types/AddressTypeEnum";
import {
  PaymentContext,
  PaymentContextProps,
} from "../../Contexts/PaymentContext";
import { AdminContext, AdminContextProps } from "../../Contexts/AdminContext";
import { isEmpty } from "../../utils/isEmpty";
import BackIcon from "../../assets/back.png";
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "../../Router";
import { LoadingSpinner } from "../Loading";
import { Wallet } from '@mercadopago/sdk-react';


interface Props {
  style?: any;
  isAdmin?: boolean;
  isPreview?: boolean;
  isNextEnabled?: boolean;
  showAddressForm?: boolean;
  showPaymentForm?: boolean;
  orderDetailsForm?: any;
  onClickNext?: any;
  onClickBack?: any;
  loadingNext?: boolean;
  preferenceId?: string | null;
}

export const OrderSummary = ({
  style,
  isAdmin,
  isPreview,
  isNextEnabled,
  showAddressForm,
  showPaymentForm,
  orderDetailsForm,
  onClickNext,
  onClickBack,
  loadingNext,
  preferenceId,
}: Props) => {
  const navigate = useNavigate();

  const { isDesktopOrLaptop } = useResponsive();
  const {
    sheetType,
    sheetSize,
    color,
    ringType,
    side,
    copies,
    shippingPrice,
    loadingShipping,
    totalPrice,
    loadingPrice,
  }: OrderContextProps = useContext<any>(OrderContext);

  const { fileUpload }: FileUploadContextProps =
    useContext<any>(FileUploadContext);

  const { addressType, addressSend, addressPickup }: AddressContextProps =
    useContext<any>(AddressContext);

  const { paymentForm }: PaymentContextProps = useContext<any>(PaymentContext);

  const { selectedOrder }: AdminContextProps = useContext<any>(AdminContext);
  const { pathname } = useLocation();

  const isStep4 = pathname === routes.clientPayment.path

  const title = isPreview ? selectedOrder.tracking_code : "Tu Pedido";
  const fileCount = isPreview
    ? selectedOrder.qty_files
    : fileUpload.uploadedFiles.length;
  const fileCountText = fileCount > 1 ? "Archivos" : "Archivo";
  const pageCount = isPreview
    ? selectedOrder.qty_pages
    : fileUpload.uploadedPages;
  const pageCountText = pageCount > 1 ? "Páginas" : "Página";
  const sheetSizeText = isPreview
    ? selectedOrder.hoja
    : sheetSize?.selectedSheetSize?.text;
  const colorText = isPreview
    ? selectedOrder.color
    : color?.selectedColor?.text;
  const fazText = isPreview ? selectedOrder.faz : side?.selectedSide?.text;
  const sheetTypeText = isPreview
    ? selectedOrder.papel
    : sheetType?.selectedSheetType?.text;
  const ringTypeText = isPreview
    ? selectedOrder.anillado
    : ringType?.selectedRingType?.text;
  const copiesText = isPreview ? selectedOrder.copies : copies.numberOfCopies;

  const isAddressPickup = isPreview
    ? !isEmpty(selectedOrder.sucursal)
    : addressType.selectedAddressType === AddressTypeEnum.PICKUP;

  const branchName = isPreview
    ? selectedOrder.sucursal
    : addressPickup.selectedBranch?.name;
  const branchAddress = isPreview
    ? selectedOrder.sucursal_address
    : addressPickup.selectedBranch?.address;

  const addressText = isPreview
    ? selectedOrder.address_send?.address
    : addressSend.addressSendForm?.address;
  const addressDetails = isPreview
    ? selectedOrder.address_send?.addressDetails
    : addressSend.addressSendForm?.addressDetails;
  const addressLocation = isPreview
    ? selectedOrder.address_send?.location
    : addressSend.addressSendForm?.location;
  const addressProvince = isPreview
    ? selectedOrder.address_send?.province.text
    : addressSend.addressSendForm?.province?.text;
  const addressPostalCode = isPreview
    ? selectedOrder.address_send?.postalCode
    : addressSend.addressSendForm?.postalCode;

  const paymentName = isPreview
    ? selectedOrder.user_data?.name
    : paymentForm.name;
  const paymentDni = isPreview ? selectedOrder.user_data?.dni : paymentForm.dni;
  const paymentEmail = isPreview
    ? selectedOrder.user_data?.email
    : paymentForm.email;
  const paymentPhone = isPreview
    ? selectedOrder.user_data?.phoneNumber
    : paymentForm.phoneNumber;


  const getShippingPrice = () => {
    const argentinePeso = new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    });

    if (isPreview) return selectedOrder.shipping_value ? argentinePeso.format(selectedOrder.shipping_value) : '--';

    return shippingPrice ? argentinePeso.format(shippingPrice) : "--";
  };

  const getTotalPrice = () => {
    const argentinePeso = new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    });

    if (isPreview) return argentinePeso.format(selectedOrder.total_value);

    return totalPrice ? argentinePeso.format(totalPrice) : "--";
  };

  const getTotalPriceWithShipping = () => {
    const argentinePeso = new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    });

    if (isPreview) return argentinePeso.format(selectedOrder.total_value + (selectedOrder.shipping_value ? selectedOrder.shipping_value : 0));

    return totalPrice ? argentinePeso.format(totalPrice + (shippingPrice ? shippingPrice : 0)) : "--";
  };

  const getTitlePrefix = () => {
    if (!isAdmin && !isPreview) {
      return (
        <div className="flex-row-between">
          <Title>Tu Pedido</Title>
        </div>
      );
    }

    if (isAdmin) {
      return (
        <div className="flex-row">
          <img
            src={BackIcon}
            alt=""
            onClick={() => navigate(routes.adminOrders.path)}
            className="pointer"
            style={{ width: "20px", height: "20px", marginRight: "10px" }}
          />
          <Title>{title} </Title>
        </div>
      );
    }

    if (isPreview) {
      return (
        <div className="flex-row-between">
          <Title>Pedido</Title>
          <Title>{title} </Title>
        </div>
      );
    }
  };
  
  const onReadyPayWithMP = () => {
    // Override MercadoPago blue button
    // setTimeout will give some slack to load content
    setTimeout(() => {
      // Remove possibly duplicated buttons
      const buttons: NodeList = document.querySelectorAll('#walletBrick_container > div') 
      if (buttons.length > 1) {
        buttons.forEach((node: Node, index: number) => {
          if (index > 0)
            node?.parentNode?.removeChild(node)
        })
      }
    }, 100)
  }

  return (
    <div
      style={style}
      className={
        isDesktopOrLaptop ? "order-summary-desktop" : "order-summary-mobile"
      }
    >
      <div className="top-content">
        {getTitlePrefix()}

        <div className="files-pages-section">
          <div className="files">
            <Header>{fileCount}</Header>
            <Text bold>{fileCountText}</Text>
          </div>

          <div className="pages">
            <Header>{pageCount}</Header>
            <Text bold>{pageCountText}</Text>
          </div>
        </div>

        {orderDetailsForm ? (
          orderDetailsForm
        ) : (
          <div className="read-only-order-details">
            <div className="details-row">
              <Subheader light secondary>
                Hoja
              </Subheader>
              <Subheader>{sheetSizeText}</Subheader>
            </div>

            <div className="details-row">
              <Subheader light secondary>
                Impresión
              </Subheader>
              <Subheader>{colorText}</Subheader>
            </div>

            <div className="details-row">
              <Subheader light secondary>
                Faz
              </Subheader>
              <Subheader>{fazText}</Subheader>
            </div>

            <div className="details-row">
              <Subheader light secondary>
                Papel
              </Subheader>
              <Subheader>{sheetTypeText}</Subheader>
            </div>

            <div className="details-row">
              <Subheader light secondary>
                Anillado
              </Subheader>
              <Subheader>{ringTypeText}</Subheader>
            </div>

            <div className="details-row">
              <Subheader light secondary>
                Copias
              </Subheader>
              <Subheader>{copiesText}</Subheader>
            </div>
          </div>
        )}

        {showAddressForm && (
          <>
            <div>
              {isAddressPickup ? (
                <div className="flex-column">
                  <Text bold>Retiro por local</Text>
                  <Text>{branchName}</Text>
                  <Text>{branchAddress}</Text>
                </div>
              ) : (
                <div className="flex-column">
                  <Text bold>Envío</Text>
                  <Text>{addressText}</Text>
                  <Text>{addressDetails}</Text>
                  <Text>{addressLocation}</Text>
                  <Text>{addressProvince}</Text>
                  <Text>{addressPostalCode}</Text>
                </div>
              )}
            </div>
          </>
        )}

        {showPaymentForm && (
          <>
            <div className="flex-column" style={{ marginTop: "10px" }}>
              <Text bold>{paymentName}</Text>
              <Text>{paymentDni}</Text>
              <Text>{paymentEmail}</Text>
              <Text>{paymentPhone}</Text>
            </div>
          </>
        )}
      </div>
      <div className="bottom-content">
      <div className="details-row" style={{ alignItems: "center" }}>
          <Subheader secondary light>
            Costo de envío
          </Subheader>

          {loadingShipping ? (
            <LoadingSpinner width={20} height={20} />
          ) : (
            <Subheader>{getShippingPrice()}</Subheader>
          )}
        </div>

        <div className="details-row" style={{ alignItems: "center" }}>
          <Subheader secondary light>
            Total
          </Subheader>

          {loadingPrice ? (
            <LoadingSpinner width={20} height={20} />
          ) : (
            <Subheader>{getTotalPrice()}</Subheader>
          )}
        </div>

        <div className="details-row" style={{ alignItems: "center" }}>
          <Subheader secondary light>
            Total con envío
          </Subheader>

          {loadingPrice ? (
            <LoadingSpinner width={20} height={20} />
          ) : (
            <Subheader>{getTotalPriceWithShipping()}</Subheader>
          )}
        </div>
        
        {onClickNext && (
          preferenceId && isStep4 ? (
            <div className="button-wallet-button">
              <Wallet
                onReady={onReadyPayWithMP}
                initialization={{ preferenceId: preferenceId, redirectMode: 'self' }}
                customization={{
                  texts: { action: 'pay', valueProp: 'security_details' },
                  visual: { hideValueProp: true },
                }}
              />
            </div>
          ) : (
            <Button
              loading={loadingNext}
              disabled={!isNextEnabled}
              fullwidth
              onClick={onClickNext}
            >
              {/* Check if it's first step */}
              {onClickBack ? 'Continuar' : 'Realizar Pedido'}
            </Button>
          )
        )}


        {onClickBack && (
          <Button
            disabled={loadingNext}
            style={{ marginTop: "10px" }}
            fullwidth
            outlined
            grey
            onClick={onClickBack}
          >
            Volver
          </Button>
        )}
      </div>
    </div>
  );
};
